import GoogleMapReact from "google-map-react";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useStyles } from "../style";
import { useField } from "react-final-form";
import {
  calculateArrivalTimes,
  calculateStartTimeAndEndTime,
  getAddressLocation,
  getExistingRoutePolyline,
  getRoutePolyline,
  getSelectedRoutePlanVersion,
  removeSchoolLocationFromRouteLocation,
  reOptimiseRouteLocation,
  updateRoutePlanVersion,
} from "../tools";
import { Button, useNotify } from "react-admin";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import { findIndex } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../components/layout/Menu";
import {
  changeSelectedAddressFromMarker,
  updateRouteTimeData,
} from "../../../redux/navmode.action";
import Alert from "@material-ui/lab/Alert";
import DroppedLocationMarker from "./DroppedLocationMarker";
import LocationMarker from "./LocationMarker";
import SchoolMarker from "./SchoolMarker";

interface RoutePlanMapProps {
  mode: "create" | "update" | "show";
  setIsLoadingMap: any;
  setProgressMap: any;
}

const RoutePlanMap: FC<RoutePlanMapProps> = ({
  mode,
  setIsLoadingMap,
  setProgressMap,
}) => {
  const classes = useStyles();

  const [mapState, setMapState] = useState({
    zoom: 12, // Default zoom level
  });
  const [mapInstance, setMapInstance] = useState<any>(null);
  const [mapsLayoutInstance, setMapsLayout] = useState<any>(null);
  const [polylines, setPolylines] = useState<any>([]);

  const [draggable, setDraggable] = useState<any>(true);
  const [tempMarkerForMoving, setTempMarkerForMoving] = useState<any>(null);
  const [open, setOpen] = useState<boolean>(false);

  const [isLoadingOptimiseRoute, setIsLoadingOptimiseRoute] =
    useState<boolean>(false);

  const [isLoadingAddress, setIsLoadingAddress] = useState<boolean>(false);
  const [newAddress, setNewAddress] = useState<any>(null);

  const schoolField = useField("school");
  const routesField = useField("routes");
  const droppedLocationsField = useField("droppedLocations");

  const busScheduleField = useField("busSchedule");
  const busScheduleIdField = useField("busScheduleId");

  const maxStopTimeField = useField("maxStopTime");

  // Ref to store AbortController so it persists across renders
  const abortControllerRef = useRef<AbortController | null>(null);

  const notify = useNotify();

  const selectedRouteState = useSelector(
    (state: AppState) => state.navMode.route,
  );

  const dispatch = useDispatch();

  const selectedRoutePlanVersion = getSelectedRoutePlanVersion();

  const handleOpenDialogChangeAddress = () => {
    setOpen(true);
  };

  const handleCloseDialogChangeAddress = () => {
    setOpen(false);
    setTempMarkerForMoving(null);
    setNewAddress(null);
  };

  const handleMapChange = ({ zoom }: any) => {
    setMapState((prevState) => ({
      ...prevState,
      zoom,
    }));
  };

  const handleApiLoaded = useCallback(
    (map: any, maps: any) => {
      // use map and maps objects
      setMapInstance(map);
      setMapsLayout(maps);
    },
    [setMapInstance, setMapsLayout],
  );

  const onMouseDown = () => {
    if (!selectedRouteState) {
      return;
    }

    setDraggable(false);
  };

  const onMouseMove = (_childKey: any, childProps: any, mouse: any) => {
    if (!selectedRouteState) {
      return;
    }

    setTempMarkerForMoving({
      lat: mouse.lat,
      lng: mouse.lng,
      data: childProps.data,
    });
  };

  const onMouseUp = () => {
    if (!selectedRouteState) {
      return;
    }

    setDraggable(true);

    if (!tempMarkerForMoving) {
      return;
    }

    setIsLoadingAddress(true);
    getAddressLocation(tempMarkerForMoving.lat, tempMarkerForMoving.lng).then(
      (address) => {
        setIsLoadingAddress(false);
        const { BLOCK, POSTALCODE, ROAD, BUILDINGNAME, LATITUDE, LONGITUDE } =
          address;

        const addressData = {
          blockNumber: BLOCK,
          building: BUILDINGNAME,
          lat: LATITUDE,
          lng: LONGITUDE,
          postalCode: POSTALCODE,
          street: ROAD,
          unitNumber: "",
          address: `${BLOCK} ${ROAD} ${BUILDINGNAME} ${POSTALCODE}`,
        };

        setNewAddress(addressData);
      },
    );

    handleOpenDialogChangeAddress();
  };

  const handleSaveChangeAddress = async () => {
    try {
      setIsLoadingOptimiseRoute(true);

      const findRouteLocationIndex = findIndex(
        selectedRouteState.routeLocations,
        ["order", tempMarkerForMoving.data.order],
      );

      const updatedRouteLocation = {
        ...selectedRouteState.routeLocations[findRouteLocationIndex],
        ...newAddress,
      };

      const routeLocationWithoutSchool = removeSchoolLocationFromRouteLocation(
        [
          ...selectedRouteState.routeLocations.slice(0, findRouteLocationIndex),
          updatedRouteLocation,
          ...selectedRouteState.routeLocations.slice(
            findRouteLocationIndex + 1,
          ),
        ],
        busScheduleField.input.value.type,
      );

      const optimisedRouteLocations = await reOptimiseRouteLocation(
        schoolField.input.value,
        busScheduleField.input.value,
        maxStopTimeField.input.value,
        routeLocationWithoutSchool,
      );

      const updatedRoute = {
        ...selectedRouteState,
        routeGeometries: [],
        routeLocations: optimisedRouteLocations,
      };

      dispatch(changeSelectedAddressFromMarker(updatedRoute));
      setIsLoadingOptimiseRoute(false);

      handleCloseDialogChangeAddress();
    } catch (error) {
      notify("Failed to change address", "error");
      setIsLoadingOptimiseRoute(false);
    }
  };

  const getAllRoutePolylines = async (
    routes: any,
    isDetailRouteMode = false,
  ) => {
    // Cancel previous request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this call
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const signal = controller.signal;

    try {
      await setProgressMap(1);
      await setIsLoadingMap(true);

      let totalSegments = 0;

      // First count all segments
      for (const route of routes) {
        if (route.routeGeometries.length === 0) {
          totalSegments += Math.max(0, route.routeLocations.length - 1);
        }
      }

      let fetchedSegments = 0;

      const updateProgress = () => {
        if (signal.aborted) return;

        fetchedSegments++;
        const progress = (fetchedSegments / totalSegments) * 100;
        setProgressMap(progress);
      };

      for (const route of routes) {
        if (route.routeLocations.length < 2) {
          continue;
        }

        const findRouteIndex = findIndex(routes, ["id", route.id]);

        let allPolylines: any = [];
        if (route.routeGeometries.length > 0 || mode === "show") {
          for (const routeGeometry of route.routeGeometries) {
            const polyline = await getExistingRoutePolyline(
              routeGeometry.polyline,
            );
            allPolylines = allPolylines.concat(polyline);
          }
        } else {
          if (signal.aborted) throw new Error("Operation aborted");

          const routeSegments = await getRoutePolyline(
            route.routeLocations,
            () => {
              if (signal.aborted) return;
              updateProgress();
            },
            signal,
          );

          allPolylines = routeSegments.flatMap((seg) => seg.polyline);
          const totalTimeForOneRouteInSeconds = routeSegments.reduce(
            (acc, seg) => acc + seg.totalTime,
            0,
          );

          const routeGeometriesData = routeSegments.map((seg, index) => {
            return { order: index + 1, polyline: seg.routeGeometry };
          });

          if (signal.aborted) throw new Error("Operation aborted");

          // calculate arrival times for each route locations
          const { startTime, endTime } = calculateStartTimeAndEndTime(
            totalTimeForOneRouteInSeconds,
            maxStopTimeField.input.value,
            route.routeLocations.length,
            busScheduleField.input.value.arrivalTime,
            busScheduleField.input.value.type,
          );

          if (isDetailRouteMode) {
            selectedRouteState.startTime = startTime;
            selectedRouteState.endTime = endTime;
            selectedRouteState.routeGeometries = routeGeometriesData;
            selectedRouteState.routeLocations = calculateArrivalTimes(
              route.routeLocations,
              routeSegments.map((seg) => seg.totalTime),
              maxStopTimeField.input.value,
              busScheduleField.input.value.arrivalTime,
              busScheduleField.input.value.type,
            );

            dispatch(updateRouteTimeData(selectedRouteState));
          } else {
            routesField.input.value[findRouteIndex].startTime = startTime;
            routesField.input.value[findRouteIndex].endTime = endTime;
            routesField.input.value[findRouteIndex].routeGeometries =
              routeGeometriesData;
            routesField.input.value[findRouteIndex].routeLocations =
              calculateArrivalTimes(
                route.routeLocations,
                routeSegments.map((seg) => seg.totalTime),
                maxStopTimeField.input.value,
                busScheduleField.input.value.arrivalTime,
                busScheduleField.input.value.type,
              );
          }
        }

        await renderPolyline(
          mapInstance,
          mapsLayoutInstance,
          allPolylines,
          findRouteIndex,
          route.routeColor,
        );

        if (mode === "update") {
          updateRoutePlanVersion(
            selectedRoutePlanVersion.id,
            routesField.input.value,
            droppedLocationsField.input.value,
          );
        }
      }

      if (!signal.aborted) {
        await setIsLoadingMap(false);
      }
    } catch (error) {
      if (signal.aborted) {
        console.log("Request was cancelled");
        await setIsLoadingMap(false);
        return;
      }

      await setIsLoadingMap(false);
      await notify("Failed to load routeline", "warning");
      console.error(error);
    }
  };

  const renderPolyline = async (
    map: any,
    maps: any,
    paths: any[],
    indexRoute: number,
    routeColor: any,
  ) => {
    if (!paths.length) return;

    let geodesicPolyline = new maps.Polyline({
      path: paths,
      geodesic: true,
      strokeColor: `${routeColor.colorTwo}`,
      strokeOpacity: 1.0,
      strokeWeight: 5,
      icons: [
        {
          offset: "100%",
          repeat: "50px",
        },
      ],
    });

    geodesicPolyline.setMap(map);

    setPolylines((prev: any) => [...prev, { geodesicPolyline, indexRoute }]);
  };

  const clearPolyline = () => {
    if (polylines.length > 0) {
      polylines.forEach((polyline: any) => {
        polyline.geodesicPolyline.setMap(null);
      });

      setPolylines([]);
    }
  };

  useEffect(() => {
    if (!mapsLayoutInstance || !mapInstance) return;

    clearPolyline();

    if (selectedRouteState) {
      getAllRoutePolylines([selectedRouteState], true);
      return;
    }

    if (routesField.input.value.length > 0) {
      getAllRoutePolylines(routesField.input.value);
      return;
    }
  }, [
    routesField.input.value,
    selectedRouteState,
    mapsLayoutInstance,
    mapInstance,
  ]);

  useEffect(() => {
    clearPolyline();

    // Cancel previous one map request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsLoadingMap(false);
    }
  }, [busScheduleIdField.input.value]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <div className={classes.mapWrapper}>
      {selectedRouteState &&
        mode !== "show" &&
        schoolField.input.value
          .isCustomPickupAndDropoffLocationAllowedForRouteplan && (
          <Alert
            severity="info"
            style={{ position: "absolute", top: "0", zIndex: 99 }}
          >
            <b>Info : </b> You able to change the address by moving the marker.
          </Alert>
        )}

      <GoogleMapReact
        bootstrapURLKeys={{
          key: "AIzaSyDYGvy_HvsUfvJ_7_d4PmOH201jIFNn-iM",
        }}
        defaultCenter={{
          lat: 1.35896,
          lng: 103.852309,
        }}
        defaultZoom={12}
        draggable={draggable}
        onChildMouseDown={
          mode === "show" ||
          !schoolField.input.value
            .isCustomPickupAndDropoffLocationAllowedForRouteplan
            ? null
            : onMouseDown
        }
        onChildMouseMove={
          mode === "show" ||
          !schoolField.input.value
            .isCustomPickupAndDropoffLocationAllowedForRouteplan
            ? null
            : onMouseMove
        }
        onChildMouseUp={
          mode === "show" ||
          !schoolField.input.value
            .isCustomPickupAndDropoffLocationAllowedForRouteplan
            ? null
            : onMouseUp
        }
        onChange={handleMapChange}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
      >
        {selectedRouteState
          ? selectedRouteState.routeLocations.map(
              (location: any, index: number) => {
                return (
                  <LocationMarker
                    lat={
                      tempMarkerForMoving
                        ? tempMarkerForMoving.lat
                        : location.lat
                    }
                    lng={
                      tempMarkerForMoving
                        ? tempMarkerForMoving.lng
                        : location.lng
                    }
                    key={`${index}`}
                    zoom={mapState.zoom}
                    order={
                      tempMarkerForMoving
                        ? tempMarkerForMoving.data.order
                        : location.order
                    }
                    colorOne={selectedRouteState.routeColor.colorOne}
                    colorTwo={selectedRouteState.routeColor.colorTwo}
                    data={{
                      ...location,
                      routeName: selectedRouteState.routeName,
                    }}
                    isDetailRoute
                  />
                );
              },
            )
          : routesField.input.value.map((route: any) =>
              route.routeLocations.map((location: any, index: number) => {
                return (
                  <LocationMarker
                    lat={location.lat}
                    lng={location.lng}
                    key={`location-marker${index}`}
                    zoom={mapState.zoom}
                    order={location.order}
                    colorOne={route.routeColor.colorOne}
                    colorTwo={route.routeColor.colorTwo}
                    data={{ ...location, routeName: route.routeName }}
                    isDetailRoute={false}
                  />
                );
              }),
            )}

        {!selectedRouteState &&
          droppedLocationsField.input.value.map(
            (location: any, index: number) => {
              return (
                <DroppedLocationMarker
                  lat={location.lat}
                  lng={location.lng}
                  key={`location-marker${index}`}
                  zoom={mapState.zoom}
                  order={index + 1}
                  data={location}
                />
              );
            },
          )}

        {schoolField.input.value && (
          <SchoolMarker
            key={`school-marker`}
            lat={schoolField.input.value.lat}
            lng={schoolField.input.value.lng}
            record={schoolField.input.value}
            zoom={mapState.zoom}
          />
        )}
      </GoogleMapReact>

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="md"
      >
        <DialogTitle id="alert-dialog-title">Warning</DialogTitle>
        <DialogContent>
          {isLoadingAddress ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              py={4}
            >
              <CircularProgress size={60} />
              <Typography variant="body2">Please Wait...</Typography>
            </Box>
          ) : (
            <DialogContentText color="initial">
              Are you sure to move from{" "}
              <b>
                {tempMarkerForMoving ? tempMarkerForMoving.data.address : ""}
              </b>{" "}
              to <b>{newAddress ? newAddress.address : ""}</b> ?
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialogChangeAddress}
            color="secondary"
            label="Cancel"
          />
          <Button
            onClick={handleSaveChangeAddress}
            color="primary"
            label="Proceed"
            startIcon={
              isLoadingOptimiseRoute && (
                <CircularProgress size={20} style={{ color: "#FFFFFF" }} />
              )
            }
          />
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default RoutePlanMap;
